import { useEffect, useState, useMemo } from "react";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Trash, Key, Copy, CheckCircle, UploadSimple, FileText, Printer, Certificate, ArrowsMerge, PaperPlaneTilt, Prohibit, IdentificationCard } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import ImportModal from "@/components/ImportModal";
import MergeWorkersModal from "@/components/MergeWorkersModal";
import ResumeModal from "@/components/ResumeModal";
import SalesRepModal from "@/components/SalesRepModal";
import { QuickView, QVRow, qvRowClick } from "@/components/QuickView";
import { useTableSort } from "@/lib/useTableSort";
import { lastFirst, lastNameKey } from "@/lib/names";
import { SortableTh } from "@/components/SortableTh";
import { StatusFilter } from "@/components/StatusFilter";
import { usePersistedState } from "@/lib/usePersistedState";
import { useColumnPrefs } from "@/lib/useColumnPrefs";
import { ColumnChooser } from "@/components/ColumnChooser";
import { toast } from "sonner";
import { money } from "@/lib/money";

const empty = { name: "", email: "", phone: "", address: "", dba: "", tax_id: "", ach_routing: "", ach_account: "", ach_account_type: "checking", status: "active", special_comp: [], exclude_from_projects: false, exclude_reason: "" };

const WK_COLUMNS = [
  { key: "name", label: "Name", locked: true },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Address" },
  { key: "status", label: "Status" },
];

const COMP_TYPES = [
  { value: "weekly_flat", label: "Flat weekly rate" },
  { value: "salary", label: "Salary (weekly)" },
  { value: "distribution", label: "Owner distribution (weekly target)" },
  { value: "per_billed_hour", label: "$ per billed hour (commission)" },
];

const compLabel = (c, projects = []) => {
  const amt = money(c.amount);
  if (c.type === "per_billed_hour") {
    const names = (c.project_ids || []).map((id) => projects.find((p) => p.id === id)?.name || "?").join(", ");
    return `${amt}/billed hr — ${names || "no projects selected"}`;
  }
  const t = COMP_TYPES.find((x) => x.value === c.type)?.label || c.type;
  return `${t}: ${amt}/week`;
};

const IMPORT_FIELDS = [
  { key: "name", label: "Name", match: ["name", "worker"] },
  { key: "email", label: "Email", match: ["email", "e-mail"] },
  { key: "phone", label: "Phone", match: ["phone", "cell", "mobile", "tel"] },
  { key: "dba", label: "DBA / LLC", match: ["dba", "llc", "business", "company"] },
  { key: "tax_id", label: "SSN / EIN", match: ["ssn", "ein", "tin", "tax", "social"] },
  { key: "street", label: "Street", match: ["street", "address"] },
  { key: "street2", label: "Street 2 / Apt", match: ["street 2", "address 2", "address2", "apt", "suite", "unit", "line 2", "second"] },
  { key: "city", label: "City", match: ["city", "town"] },
  { key: "state", label: "State", match: ["state"] },
  { key: "zip", label: "Zip", match: ["zip", "postal"] },
];

const certTone = (w) => {
  const certs = w.certs || [];
  if (!certs.length) return null;
  const days = certs.filter((c) => c.expires_on).map((c) => Math.floor((new Date(c.expires_on) - new Date()) / 86400000));
  if (days.some((d) => d < 0)) return "text-rose-500";
  if (days.some((d) => d <= 30)) return "text-amber-500";
  return "text-emerald-600";
};

export default function Workers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [err, setErr] = useState("");
  const [credentials, setCredentials] = useState(null);  // { email, temp_password, worker_name, was_created }
  const [importOpen, setImportOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [viewW, setViewW] = useState(null);
  const [resumeFor, setResumeFor] = useState(null);
  const [repFor, setRepFor] = useState(null);
  const isAdmin = user.role === "admin";
  const isManager = user.role === "manager";
  const readOnly = !isAdmin; // admin-only actions (status, login, exclude, delete, merge, import)
  const canEdit = isAdmin || isManager; // managers: contact fields only
  const canResume = isAdmin || isManager;
  const [taxReqBusy, setTaxReqBusy] = useState("");
  const shortDT = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
  const missingTax = (w) => isAdmin && (!w.tax_id_masked || !w.address);
  const requestTaxInfo = async (w) => {
    if (taxReqBusy) return;
    const again = w.tax_info_requested_at ? `\n\nAlready requested ${shortDT(w.tax_info_requested_at)}. Send again?` : "";
    if (!window.confirm(`Ask ${w.name} for their missing ${[!w.tax_id_masked && "SSN/EIN", !w.address && "mailing address"].filter(Boolean).join(" and ")}?\n\nThey get an email${w.phone ? " + text" : ""} with a secure single-use link (14 days). You\u2019ll be emailed when they submit.${again}`)) return;
    setTaxReqBusy(w.id);
    try {
      const { data } = await api.post(`/workers/${w.id}/request-tax-info`);
      toast.success(`Tax info request sent to ${w.name} — ${data.email_sent ? "email" : "no email"}${data.sms_sent ? " + text" : ""}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setTaxReqBusy(""); }
  };
  const [excludeFor, setExcludeFor] = useState(null);
  const [excludeReason, setExcludeReason] = useState("");
  const { vis, hiddenSet, toggleColumn, resetColumns } = useColumnPrefs("workers", WK_COLUMNS);
  const wkVisCount = WK_COLUMNS.length - hiddenSet.size + 1;

  const load = () => api.get("/workers").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  const [statusFilter, setStatusFilter] = usePersistedState("sos-filter-workers", "active");
  const filteredRows = useMemo(() => statusFilter === "all" ? rows : rows.filter((r) => r.status === statusFilter), [rows, statusFilter]);
  const statusCounts = useMemo(() => ({
    all: rows.length,
    active: rows.filter((r) => r.status === "active").length,
    inactive: rows.filter((r) => r.status === "inactive").length,
  }), [rows]);
  const { sorted, sortKey, sortDir, toggle } = useTableSort(filteredRows, {
    initialKey: "name",
    storageKey: "sos-sort-workers",
    getValue: (row, key) => (key === "name" ? lastNameKey(row.name) : row[key]),
  });

  const save = async (e) => {
    e.preventDefault(); setErr("");
    const payload = {
      ...form,
      special_comp: (form.special_comp || []).map((c) => ({
        type: c.type, amount: parseFloat(c.amount) || 0, project_ids: c.project_ids || [],
      })),
    };
    try {
      if (editingId) await api.put(`/workers/${editingId}`, payload);
      else await api.post("/workers", payload);
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.duplicates) {
        const list = detail.duplicates
          .map((x) => `• ${x.name}${x.email ? ` — ${x.email}` : ""}${x.phone ? ` — ${x.phone}` : ""} (${x.status})`)
          .join("\n");
        if (window.confirm(`Possible duplicate — a worker like this already exists:\n\n${list}\n\nCreate "${payload.name}" anyway?\n\nTip: if this IS the same person, cancel and use Merge duplicates instead.`)) {
          try {
            await api.post("/workers", { ...payload, allow_duplicate: true });
            setOpen(false); setForm(empty); setEditingId(null); load();
          } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
        }
        return;
      }
      setErr(formatApiErrorDetail(detail) || e.message);
    }
  };
  const startEdit = (w) => { setEditingId(w.id); setForm({ ...empty, ...w, ach_routing: "", ach_account: "", tax_id: "" }); setOpen(true); };

  const del = async (w) => {
    if (!window.confirm(`Delete worker "${w.name}" permanently?\n\nThis cannot be undone. If they have any timesheets or assignments, the delete will be refused — set them to Inactive instead.`)) return;
    try {
      await api.delete(`/workers/${w.id}`);
      toast.success(`Deleted ${w.name}.`);
      load();
    } catch (e) {
      const msg = e.response?.data?.detail?.toString() || e.message;
      // Special case: the ONLY blocker is a linked contractor login. Offer to
      // cascade-delete both records in one shot. If there are OTHER blockers
      // too (timesheets / assignments), surface the raw error as-is.
      const loginMatch = msg.match(/linked contractor user \(([^)]+)\)/);
      const hasOtherBlockers = /\d+\s+(timesheet|assignment)/i.test(msg);
      if (loginMatch && !hasOtherBlockers) {
        const loginEmail = loginMatch[1];
        if (!window.confirm(`This worker has a linked contractor login: ${loginEmail}\n\nDelete BOTH the worker record AND the login? This cannot be undone.`)) return;
        try {
          await api.delete(`/workers/${w.id}?with_login=true`);
          toast.success(`Deleted ${w.name} and login (${loginEmail}).`);
          load();
        } catch (e2) {
          toast.error(e2.response?.data?.detail?.toString() || e2.message);
        }
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="tinylabel">Master data</div>
          <h1 className="text-3xl font-black">Workers</h1>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {isAdmin && <button data-testid="merge-workers-btn" onClick={() => setMergeOpen(true)}
                    className="h-11 px-4 border border-slate-300 hover:bg-slate-50 font-semibold rounded-md flex items-center gap-2 transition-colors">
              <ArrowsMerge size={18} weight="bold" /> Merge duplicates
            </button>}
            {isAdmin && <button data-testid="import-workers-btn" onClick={() => setImportOpen(true)}
                    className="h-11 px-4 border border-slate-300 hover:bg-slate-50 font-semibold rounded-md flex items-center gap-2 transition-colors">
              <UploadSimple size={18} weight="bold" /> Import
            </button>}
            <button data-testid="new-worker-btn" onClick={() => { setEditingId(null); setForm(empty); setOpen(true); }}
                    className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md flex items-center gap-2 transition-colors">
              <Plus size={18} weight="bold" /> New worker
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 flex items-center justify-between gap-3">
        <StatusFilter
          value={statusFilter}
          onChange={setStatusFilter}
          counts={statusCounts}
          options={[
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
            { value: "all", label: "All" },
          ]}
        />
        <ColumnChooser columns={WK_COLUMNS} hiddenSet={hiddenSet} onToggle={toggleColumn} onReset={resetColumns} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <SortableTh label="Name" colKey="name" activeKey={sortKey} direction={sortDir} onToggle={toggle} />
              {vis("email") && <SortableTh label="Email" colKey="email" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              {vis("phone") && <SortableTh label="Phone" colKey="phone" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              {vis("address") && <th className="text-left px-4 py-3">Address</th>}
              {vis("status") && <SortableTh label="Status" colKey="status" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td className="px-4 py-6 text-slate-400" colSpan={wkVisCount}>No workers yet.</td></tr>}
            {sorted.map((w) => (
              <tr key={w.id} onClick={(e) => !readOnly && qvRowClick(e, () => setViewW(w))}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${!readOnly ? "cursor-pointer" : ""}`} data-testid={`worker-row-${w.id}`}>
                <td className="px-4 py-2 font-medium whitespace-nowrap">
                  {user.role !== "manager" ? (
                    <Link to={`/workers/${w.id}`} onClick={(e) => e.stopPropagation()}
                      data-testid={`worker-name-link-${w.id}`}
                      title="View full history"
                      className="hover:text-green-700 hover:underline">{lastFirst(w.name)}</Link>
                  ) : lastFirst(w.name)}
                  {w.dba && <span className="ml-2 text-xs font-normal text-slate-400" data-testid={`worker-dba-${w.id}`}>({w.dba})</span>}
                  {w.is_sales_rep && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 align-middle"
                      data-testid={`worker-sales-rep-badge-${w.id}`} title={`Sales rep — $${w.sales_comp?.rate_per_hour ?? 0}/hr, $${w.sales_comp?.weekly_draw ?? 0}/wk draw`}>
                      Sales rep
                    </span>
                  )}
                  {w.exclude_from_projects && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-rose-600 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5 align-middle"
                      data-testid={`worker-excluded-badge-${w.id}`}
                      title={w.exclude_reason ? `Do Not Use: ${w.exclude_reason}` : "Do Not Use — hidden from sales reps, left out of recruitment, no packets"}>
                      Do not use
                    </span>
                  )}
                </td>
                {vis("email") && <td className="px-4 py-2 text-slate-600">{w.email || "—"}</td>}
                {vis("phone") && <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{w.phone}</td>}
                {vis("address") && <td className="px-4 py-2 text-slate-500 text-xs max-w-[220px] truncate" title={w.address || ""}>{w.address || "—"}</td>}
                {vis("status") && <td className="px-4 py-2"><StatusPill status={w.status} /></td>}
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {canResume && (
                    <button data-testid={`resume-btn-${w.id}`}
                            onClick={() => setResumeFor(w)}
                            title={w.resume ? `Resume on file: ${w.resume.filename}` : "Upload resume"}
                            className={`align-middle mr-2 ${w.resume ? "text-emerald-600 hover:text-emerald-800" : "text-slate-300 hover:text-slate-500"}`}>
                      <FileText size={16} weight={w.resume ? "fill" : "bold"} />
                    </button>
                  )}
                  {canResume && certTone(w) && (
                    <button data-testid={`cert-badge-${w.id}`}
                            onClick={() => setResumeFor(w)}
                            title={`${(w.certs || []).length} certification(s) on file`}
                            className={`align-middle mr-2 ${certTone(w)}`}>
                      <Certificate size={16} weight="fill" />
                    </button>
                  )}
                  {!readOnly && (
                    <>
                    <button data-testid={`toggle-worker-${w.id}`}
                            onClick={async () => {
                              const next = w.status === "active" ? "inactive" : "active";
                              try {
                                await api.put(`/workers/${w.id}`, { ...w, status: next, ach_routing: "", ach_account: "" });
                                toast.success(`${w.name} → ${next}`);
                                load();
                              } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
                            }}
                            title={w.status === "active" ? "Set inactive" : "Set active"}
                            className={`align-middle whitespace-nowrap text-xs font-semibold px-2 py-1 rounded border ${w.status === "active" ? "border-amber-200 text-amber-700 hover:bg-amber-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
                      {w.status === "active" ? "Set inactive" : "Set active"}
                    </button>
                    <button data-testid={`login-worker-${w.id}`}
                            onClick={async () => {
                              const msg = w.has_login
                                ? `Reset the login password for ${w.name} and email them the new temp password?\n\nA welcome email will be sent to ${w.email} with the fresh credentials, home-screen install steps, and how to submit their first timesheet. A copy is BCC'd to billing@safetyonsitesolutions.com.`
                                : `Create a contractor login for ${w.name} and email them a welcome guide?\n\nA welcome email will be sent to ${w.email} with login credentials, home-screen install steps for iPhone & Android, and how to submit their first timesheet. A copy is BCC'd to billing@safetyonsitesolutions.com.`;
                              if (!window.confirm(msg)) return;
                              try {
                                const { data } = await api.post(`/workers/${w.id}/create-login`);
                                setCredentials(data);
                                load();
                              } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
                            }}
                            title={w.has_login ? "Reset password" : "Create contractor login"}
                            className={`ml-2 align-middle whitespace-nowrap text-xs font-semibold px-2 py-1 rounded border inline-flex items-center gap-1 ${w.has_login ? "border-sky-200 text-sky-700 hover:bg-sky-50" : "border-teal-200 text-teal-700 hover:bg-teal-50"}`}>
                      <Key size={12} weight="bold" />
                      {w.has_login ? "Reset PW" : "Set login"}
                    </button>
                    {w.has_login && (
                      <button data-testid={`resend-welcome-${w.id}`}
                              onClick={async () => {
                                if (!window.confirm(`Resend the welcome reminder to ${w.name}?\n\nEmail goes to ${w.email}${w.phone ? ` and a text to ${w.phone}` : ""}. Their password is NOT changed — the reminder points them at their original welcome email for the temp password.`)) return;
                                try {
                                  const { data } = await api.post(`/workers/${w.id}/nudge-login`);
                                  toast.success(`Welcome reminder sent to ${data.email}${data.sms_sent ? " + text" : ""}.`);
                                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
                              }}
                              title="Resend welcome email + text (password unchanged)"
                              className="ml-2 align-middle whitespace-nowrap text-xs font-semibold px-2 py-1 rounded border inline-flex items-center gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                        <PaperPlaneTilt size={12} weight="bold" /> Resend
                      </button>
                    )}
                    </>
                  )}
                  {missingTax(w) && (
                    <span className="ml-2 inline-flex flex-col items-end align-middle">
                      <button data-testid={`request-tax-info-${w.id}`} onClick={() => requestTaxInfo(w)} disabled={taxReqBusy === w.id}
                              title={`Missing ${[!w.tax_id_masked && "SSN/EIN", !w.address && "address"].filter(Boolean).join(" + ")} — send a secure link so they can fill it in.`}
                              className="whitespace-nowrap text-xs font-semibold px-2 py-1 rounded border inline-flex items-center gap-1 border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50">
                        <IdentificationCard size={12} weight="bold" /> {taxReqBusy === w.id ? "…" : (w.tax_info_requested_at ? "Re-request tax info" : "Request tax info")}
                      </button>
                      {w.tax_info_received_at && <span className="text-[10px] text-emerald-700 font-semibold" data-testid={`tax-info-received-${w.id}`}>Received {shortDT(w.tax_info_received_at)}</span>}
                      {!w.tax_info_received_at && w.tax_info_requested_at && <span className="text-[10px] text-amber-700" data-testid={`tax-info-requested-${w.id}`}>Requested {shortDT(w.tax_info_requested_at)}</span>}
                    </span>
                  )}
                  {isAdmin && <button data-testid={`sales-rep-btn-${w.id}`} onClick={() => setRepFor(w)} title="Sales rep settings & portal login"
                                      className={`ml-3 font-semibold ${w.is_sales_rep ? "text-emerald-700 hover:text-emerald-900" : "text-slate-400 hover:text-slate-700"}`}>Sales rep</button>}
                  {canEdit && <button data-testid={`edit-worker-${w.id}`} onClick={() => startEdit(w)} className="ml-3 text-green-600 hover:text-green-800 font-semibold">Edit</button>}
                  {!readOnly && (
                    w.exclude_from_projects ? (
                      <button data-testid={`unexclude-worker-${w.id}`}
                              onClick={async () => {
                                if (!window.confirm(`Clear the Do Not Use flag from ${w.name}?\n\nThey will show up for sales reps again, appear in recruitment campaign lists, and can receive assignment packets.`)) return;
                                try {
                                  await api.put(`/workers/${w.id}`, { ...w, exclude_from_projects: false, exclude_reason: "", ach_routing: "", ach_account: "" });
                                  toast.success(`Do Not Use cleared for ${w.name} — sales reps can see them again and packets can be sent.`);
                                  load();
                                } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
                              }}
                              title={`Do Not Use${w.exclude_reason ? `: ${w.exclude_reason}` : ""}. Click to clear the flag.`}
                              className="ml-2 align-middle whitespace-nowrap text-xs font-semibold px-2 py-1 rounded border inline-flex items-center gap-1 border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100">
                        <Prohibit size={12} weight="bold" /> Do not use
                      </button>
                    ) : (
                      <button data-testid={`exclude-worker-${w.id}`}
                              onClick={() => { setExcludeReason(""); setExcludeFor(w); }}
                              title="Flag Do Not Use — hides them from sales reps, leaves them out of recruitment campaigns, and blocks assignment packets"
                              className="ml-2 align-middle whitespace-nowrap text-xs font-semibold px-2 py-1 rounded border inline-flex items-center gap-1 border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-700 hover:bg-rose-50">
                        <Prohibit size={12} weight="bold" /> Do not use
                      </button>
                    )
                  )}
                  {!readOnly && (
                    <button data-testid={`delete-worker-${w.id}`} onClick={() => del(w)}
                            title="Delete worker"
                            className="ml-3 text-slate-400 hover:text-rose-600 align-middle">
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {excludeFor && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="exclude-modal">
          <div className="bg-white rounded-md w-full max-w-md p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Prohibit size={18} weight="bold" className="text-rose-600" /> Flag as Do Not Use
              </h2>
              <button type="button" onClick={() => setExcludeFor(null)} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-600">
              <b>{excludeFor.name}</b> will be hidden from sales reps, left out of recruitment campaigns, and no assignment packets can be sent to them. Undo anytime by clicking the red "Do not use" button on their row.
            </p>
            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Reason (optional — internal only)</label>
              <textarea rows={3} autoFocus value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)}
                data-testid="exclude-reason-input"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                placeholder="e.g. No-show on Client X job, June 2026" />
            </div>
            <div className="mt-4 flex gap-2">
              <button data-testid="exclude-confirm-btn"
                onClick={async () => {
                  try {
                    await api.put(`/workers/${excludeFor.id}`, { ...excludeFor, exclude_from_projects: true, exclude_reason: excludeReason.trim(), ach_routing: "", ach_account: "" });
                    toast.success(`${excludeFor.name} excluded from future campaigns.`);
                    setExcludeFor(null);
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
                }}
                className="flex-1 h-11 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-md">
                Exclude worker
              </button>
              <button onClick={() => setExcludeFor(null)} data-testid="exclude-cancel-btn"
                className="h-11 px-4 border border-slate-300 rounded-md text-slate-600 font-semibold">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="worker-modal">
          <form onSubmit={save} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingId ? "Edit worker" : "New worker"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
            </div>
            {["name", "email", "phone", "address"].map((k) => (
              <div className="mb-3" key={k}>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{k}</label>
                <input required={k === "name"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`worker-input-${k}`}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">DBA / LLC</label>
                <input value={form.dba || ""} onChange={(e) => setForm({ ...form, dba: e.target.value })}
                  data-testid="worker-input-dba"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="business name (optional)" /></div>
              {isAdmin && <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">SSN / EIN</label>
                <input value={form.tax_id || ""} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                  data-testid="worker-input-tax_id"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md"
                  placeholder={editingId ? (form.tax_id_masked ? `on file: ${form.tax_id_masked} — blank to keep` : "leave blank to keep") : "###-##-#### or ##-#######"} /></div>}
            </div>
            {isManager && <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="worker-manager-note">Tax ID, bank details, pay settings and status are managed by the office.</div>}
            {isAdmin && <>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">ACH Routing</label>
                <input value={form.ach_routing} onChange={(e) => setForm({ ...form, ach_routing: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder={editingId ? "leave blank to keep" : "9 digits"} /></div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">ACH Account</label>
                <input value={form.ach_account} onChange={(e) => setForm({ ...form, ach_account: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder={editingId ? "leave blank to keep" : ""} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Account type</label>
                <select value={form.ach_account_type} onChange={(e) => setForm({ ...form, ach_account_type: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md"><option value="checking">Checking</option><option value="savings">Savings</option></select></div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md"><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.exclude_from_projects}
                  onChange={(e) => setForm({ ...form, exclude_from_projects: e.target.checked })}
                  data-testid="worker-exclude-toggle" className="mt-1 h-4 w-4 accent-rose-600" />
                <span>
                  <span className="text-sm font-semibold text-slate-800">Exclude from future projects</span>
                  <span className="block text-xs text-slate-500">Never include this worker in recruitment campaigns.</span>
                </span>
              </label>
              {form.exclude_from_projects && (
                <input value={form.exclude_reason || ""} onChange={(e) => setForm({ ...form, exclude_reason: e.target.value })}
                  data-testid="worker-exclude-reason"
                  className="mt-2 w-full h-10 px-3 border border-slate-300 rounded-md text-sm"
                  placeholder="Reason (optional — internal only)" />
              )}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Special compensation</div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Non-project pay added automatically to every weekly pay run: flat weekly rate,
                    salary, owner distribution target, or $/billed-hour commission.
                  </p>
                </div>
                <button type="button" data-testid="add-comp-btn"
                  onClick={() => setForm({ ...form, special_comp: [...(form.special_comp || []), { type: "weekly_flat", amount: "", project_ids: [] }] })}
                  className="text-green-600 font-semibold text-sm flex items-center gap-1 shrink-0 ml-3"><Plus size={14} /> Add</button>
              </div>
              {(form.special_comp || []).map((c, i) => (
                <div key={i} className="mt-2 border border-slate-200 rounded-md p-3 bg-slate-50">
                  <div className="flex gap-2 items-center">
                    <select value={c.type} data-testid={`comp-type-${i}`}
                      onChange={(e) => { const arr = [...form.special_comp]; arr[i] = { ...arr[i], type: e.target.value }; setForm({ ...form, special_comp: arr }); }}
                      className="flex-1 h-10 px-2 border border-slate-300 rounded-md bg-white text-sm">
                      {COMP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <input required type="number" step="0.01" min="0.01" value={c.amount}
                      data-testid={`comp-amount-${i}`}
                      placeholder={c.type === "per_billed_hour" ? "$/hr" : "$/week"}
                      onChange={(e) => { const arr = [...form.special_comp]; arr[i] = { ...arr[i], amount: e.target.value }; setForm({ ...form, special_comp: arr }); }}
                      className="w-28 h-10 px-3 border border-slate-300 rounded-md font-mono text-sm" />
                    <button type="button" data-testid={`remove-comp-${i}`}
                      onClick={() => setForm({ ...form, special_comp: form.special_comp.filter((_, j) => j !== i) })}
                      className="p-2 text-red-600"><Trash size={16} /></button>
                  </div>
                  {c.type === "per_billed_hour" && (
                    <div className="mt-2">
                      <div className="text-xs font-semibold text-slate-500 mb-1">Counts all hours billed on these projects each week:</div>
                      <div className="max-h-32 overflow-y-auto border border-slate-200 rounded bg-white p-2 space-y-1">
                        {projects.filter((p) => p.status !== "completed").map((p) => (
                          <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={(c.project_ids || []).includes(p.id)}
                              data-testid={`comp-project-${i}-${p.id}`}
                              onChange={(e) => {
                                const arr = [...form.special_comp];
                                const ids = new Set(arr[i].project_ids || []);
                                if (e.target.checked) ids.add(p.id); else ids.delete(p.id);
                                arr[i] = { ...arr[i], project_ids: [...ids] };
                                setForm({ ...form, special_comp: arr });
                              }}
                              className="accent-green-600" />
                            {p.name} <span className="text-xs text-slate-400">PO {p.po_number}</span>
                          </label>
                        ))}
                      </div>
                      {(c.project_ids || []).length === 0 && (
                        <div className="text-xs text-amber-600 mt-1 font-semibold">Select at least one project — otherwise this pays $0.</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            </>}
            {err && <div className="text-red-600 text-sm mt-3">{err}</div>}
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setOpen(false)} className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50">Cancel</button>
              <button data-testid="save-worker-btn" type="submit" className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md">Save</button>
            </div>
          </form>
        </div>
      )}

      {credentials && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCredentials(null)}>
          <div className="bg-white max-w-md w-full rounded-lg shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="credentials-modal">
            <div className={`px-5 py-3 border-b flex items-center gap-2 ${credentials.was_created ? "bg-emerald-50 border-emerald-200" : "bg-sky-50 border-sky-200"}`}>
              <CheckCircle size={22} weight="fill" className={credentials.was_created ? "text-emerald-600" : "text-sky-600"} />
              <div className="font-black text-lg">
                {credentials.was_created ? "Welcome email sent" : "Password reset & emailed"}
              </div>
            </div>
            <div className="p-5">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 mb-4 flex items-start gap-2" data-testid="email-sent-banner">
                <CheckCircle size={18} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <div className="font-bold">Welcome email delivered to {credentials.email}</div>
                  <div className="text-xs mt-1">
                    Includes login credentials, home-screen install steps for iPhone &amp; Android, and how to submit their first timesheet. A copy was BCC'd to <span className="font-mono">{credentials.email_bcc || "billing@safetyonsitesolutions.com"}</span>.
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Prefer to send by text or Signal as well? Copy the credentials below.
              </p>
              <div className="space-y-2">
                <CredentialField label="Login URL" value="https://app.safetyonsitesolutions.com/login" />
                <CredentialField label="Email" value={credentials.email} />
                <CredentialField label="Temporary password" value={credentials.temp_password} mono />
              </div>
              <button data-testid="copy-all-credentials"
                      onClick={() => {
                        const text = `Login URL: https://app.safetyonsitesolutions.com/login\nEmail: ${credentials.email}\nTemp password: ${credentials.temp_password}\n\nAfter logging in, click the key icon in the top-right and change your password.`;
                        navigator.clipboard.writeText(text);
                        toast.success("All credentials copied.");
                      }}
                      className="mt-4 w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md flex items-center justify-center gap-2">
                <Copy size={16} weight="bold" /> Copy credentials (for text/Signal)
              </button>
              <button data-testid="close-credentials-modal" onClick={() => setCredentials(null)} className="mt-2 w-full h-11 border border-slate-300 rounded-md font-semibold hover:bg-slate-50">
                Done
              </button>
              <p className="text-[11px] text-slate-500 mt-3">
                Temp password isn't stored in cleartext anywhere. If it's lost before first login, click <b>Reset PW</b> on the worker row to generate + email a fresh one.
              </p>
            </div>
          </div>
        </div>
      )}

      {mergeOpen && (
        <MergeWorkersModal workers={rows} onClose={() => setMergeOpen(false)}
          onDone={() => { setMergeOpen(false); load(); }} />
      )}
      {importOpen && (
        <ImportModal title="Import workers" endpoint="/workers/import" fields={IMPORT_FIELDS} noun="worker"
                     hint={`Names pasted as “Last, First” are automatically flipped to “First Last”. Street / Apt / City / State / Zip columns are combined into one address. SSN/EIN is encrypted at rest.`}
                     onClose={() => setImportOpen(false)} onImported={load} />
      )}
      {viewW && (
        <QuickView title={viewW.name} subtitle={viewW.dba ? `DBA: ${viewW.dba}` : null} status={viewW.status}
                   onClose={() => setViewW(null)} testId="worker-quick-view">
          <QVRow label="Email" value={viewW.email} />
          <QVRow label="Phone" value={viewW.phone} mono />
          <QVRow label="Address" value={viewW.address} />
          <QVRow label="SSN / EIN" value={viewW.tax_id_masked} mono />
          <QVRow label="ACH account type" value={viewW.ach_account_type} />
          {(viewW.special_comp || []).map((c, i) => (
            <QVRow key={i} label={i === 0 ? "Special comp" : ""} value={compLabel(c, projects)} />
          ))}
          <QVRow label="Portal login" value={viewW.has_login ? "Yes" : "No"} />
          <QVRow label="Added" value={viewW.created_at ? new Date(viewW.created_at).toLocaleDateString() : null} />
          <QVRow label="Resume" value={viewW.resume ? viewW.resume.filename : "None on file"} />
          <QVRow label="Certifications" value={(viewW.certs || []).length ? `${viewW.certs.length} on file` : "None"} />
          <div className="mt-4 flex flex-wrap gap-2">
            {viewW.resume && (
              <a href={`${API_BASE}/workers/${viewW.id}/resume`} target="_blank" rel="noreferrer"
                 data-testid="qv-resume-view"
                 className="h-10 px-3 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold inline-flex items-center gap-1.5">
                <Printer size={15} weight="bold" /> View / Print resume
              </a>
            )}
            <button data-testid="qv-resume-manage"
                    onClick={() => { setResumeFor(viewW); setViewW(null); }}
                    className="h-10 px-3 rounded-md border border-green-300 text-green-700 hover:bg-green-50 text-sm font-semibold inline-flex items-center gap-1.5">
              <FileText size={15} weight="bold" /> Manage resume & certs…
            </button>
          </div>
        </QuickView>
      )}
      {resumeFor && (
        <ResumeModal worker={resumeFor} onClose={() => setResumeFor(null)} onChanged={load} />
      )}
      {repFor && (
        <SalesRepModal worker={repFor} onClose={() => setRepFor(null)} onChanged={load} />
      )}
    </div>
  );
}

function CredentialField({ label, value, mono }) {
  return (
    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded p-2">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className={`text-sm break-all ${mono ? "font-mono font-bold text-teal-700" : "text-slate-800"}`}>{value}</div>
      </div>
      <button onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copied.`); }}
              title="Copy" className="p-2 hover:bg-white rounded border border-slate-200">
        <Copy size={14} weight="bold" />
      </button>
    </div>
  );
}
